import { useEffect, useState } from "react";

/**
 * Returns true while any <form> field on the page is focused.
 * Use to hide the sticky mobile CTA so it doesn't cover the keyboard
 * or the submit button while the user is filling a form.
 */
export function useFormFocusVisible(): boolean {
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const isFormField = (el: EventTarget | null): boolean => {
      if (!el || !(el instanceof Element)) return false;
      const form = el.closest("form");
      if (!form) return false;
      const tag = el.tagName.toLowerCase();
      return tag === "input" || tag === "textarea" || tag === "select";
    };

    const onFocusIn = (e: FocusEvent) => {
      if (isFormField(e.target)) setFocused(true);
    };
    const onFocusOut = (e: FocusEvent) => {
      // Defer so a focus-shift to another field doesn't flicker
      setTimeout(() => {
        const active = document.activeElement;
        if (!isFormField(active)) setFocused(false);
      }, 50);
    };

    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  return focused;
}
